import type { Bolt_On_Discharges, Pumps } from "gql/api";

export const DISCHARGE_TUBING_DIAMETER_TOLERANCE = 0.001;

type AutomaticSelectionDiffs = {
  equipment: {
    pumps_order?: unknown;
    uphole_y_tool_equipment_tubing_order?: unknown;
  };
  equipmentDiff: object;
  pumpsDiff: object;
  wellPropertiesDiff: object;
  yToolEquipmentTubingsDiff: object;
};

function getStringIds(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((id): id is string => typeof id === "string")
    : [];
}

function objectFieldChanged(value: unknown, fields: string[]): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    fields.some(field => field in value)
  );
}

export function hasAutomaticDischargeInputChanges({
  equipment,
  equipmentDiff,
  pumpsDiff,
  wellPropertiesDiff,
  yToolEquipmentTubingsDiff
}: AutomaticSelectionDiffs): boolean {
  const equipmentMatchFields = [
    "pumps_order",
    "use_uphole_y_tool",
    "uphole_y_tool_equipment_tubing_order"
  ];
  const equipmentInputChanged = equipmentMatchFields.some(field =>
    Object.prototype.hasOwnProperty.call(equipmentDiff, field)
  );
  const topPumpXrefId = getStringIds(equipment.pumps_order)[0];
  const topPumpChanged =
    !!topPumpXrefId &&
    objectFieldChanged((pumpsDiff as Record<string, unknown>)[topPumpXrefId], [
      "pump_id",
      "journal_deleted_at"
    ]);
  const upholeTubingChanged = getStringIds(
    equipment.uphole_y_tool_equipment_tubing_order
  ).some(id =>
    objectFieldChanged(
      (yToolEquipmentTubingsDiff as Record<string, unknown>)[id],
      ["tubing_id", "journal_deleted_at"]
    )
  );

  return (
    equipmentInputChanged ||
    topPumpChanged ||
    Object.prototype.hasOwnProperty.call(wellPropertiesDiff, "top_tubing_id") ||
    Object.prototype.hasOwnProperty.call(
      wellPropertiesDiff,
      "bottom_tubing_id"
    ) ||
    Object.prototype.hasOwnProperty.call(
      wellPropertiesDiff,
      "bottom_tubing_length_feet"
    ) ||
    upholeTubingChanged
  );
}

/**
 * The pump an automatic discharge selection mounts on: the first entry of the
 * pump order with a matching xref. Mirrors the live derivation's topPump in
 * useAutomaticBoltOnDischarge.ts as a pure function, so a read-only report
 * page (Design Overview) can derive the same display value without touching
 * that composable's form-writing side effects.
 */
export function findAutomaticDischargeTopPump(
  pumpsOrder: unknown,
  pumpsMap:
    | Record<string, { r_pump?: Partial<Pumps> | null } | undefined>
    | null
    | undefined
): Partial<Pumps> | null {
  for (const xrefId of getStringIds(pumpsOrder)) {
    const xref = pumpsMap?.[xrefId];
    if (xref) return xref.r_pump || null;
  }
  return null;
}

export function computeBoltOnDischargeLabel(
  boltOnDischarge: Partial<Bolt_On_Discharges> | null | undefined
): string | null {
  if (boltOnDischarge) {
    return `${boltOnDischarge.manufacturer}  / ${boltOnDischarge.series_model}`;
  }
  return null;
}

/**
 * A null measurement means the value is unknown. A zero means someone measured
 * the part and recorded a zero. The two must not be treated alike, because a
 * zero that stands for an unknown reads as a measurement on every screen and
 * in the export. The screens print nothing for an unknown, so this test is
 * what keeps a blank cell apart from a real zero.
 */
export function isBoltOnDischargeMeasurementKnown(
  value: number | null | undefined
): boolean {
  return (
    value !== null && value !== undefined && Number.isFinite(Number(value))
  );
}

export type BoltOnDischargeMeasurementField =
  | "length_inches"
  | "weight_pounds"
  | "outer_diameter_inches";

/**
 * True only when a discharge is selected and the catalog holds no value for
 * the measurement. A scenario with no discharge contributes a real zero, so it
 * must read as zero rather than as unknown.
 */
export function isBoltOnDischargeMeasurementUnknown(
  boltOnDischarge: Partial<Bolt_On_Discharges> | null | undefined,
  field: BoltOnDischargeMeasurementField
): boolean {
  if (!boltOnDischarge) return false;
  return !isBoltOnDischargeMeasurementKnown(boltOnDischarge[field]);
}

/**
 * Finds the catalog discharges compatible with a pump and its attached tubing.
 * The spreadsheet's pump-series values are nominal housing diameters multiplied
 * by 100 (5.13 in => 513 series). This distinguishes the 513 and 538 rows even
 * though their discharge geometry is otherwise identical.
 */
export function findMatchingDischarges(
  pump: Partial<Pumps> | null | undefined,
  tubingOuterDiameterInches: number | null | undefined,
  boltOnDischarges: Bolt_On_Discharges[] | null | undefined
): Bolt_On_Discharges[] {
  const housingOd = pump?.housing_outer_diameter_inches;
  if (
    housingOd === null ||
    housingOd === undefined ||
    tubingOuterDiameterInches === null ||
    tubingOuterDiameterInches === undefined ||
    !boltOnDischarges
  ) {
    return [];
  }

  const targetHousingOd = Number(housingOd);
  const targetTubingOd = Number(tubingOuterDiameterInches);
  if (!Number.isFinite(targetHousingOd) || !Number.isFinite(targetTubingOd)) {
    return [];
  }
  const pumpSeries = String(Math.round(targetHousingOd * 100));

  return boltOnDischarges.filter(discharge => {
    if (discharge.is_hps || discharge.deleted_at) return false;

    const tubingOd = discharge.tubing_diameter_inches;
    const dischargeSeries = discharge.series_model?.match(/(\d{3,4})\s*$/)?.[1];
    if (tubingOd === null || tubingOd === undefined || !dischargeSeries) {
      return false;
    }

    const candidateTubingOd = Number(tubingOd);
    if (!Number.isFinite(candidateTubingOd)) {
      return false;
    }

    return (
      Math.abs(candidateTubingOd - targetTubingOd) <=
        DISCHARGE_TUBING_DIAMETER_TOLERANCE && dischargeSeries === pumpSeries
    );
  });
}

export function findBestMatchingDischarge(
  pump: Partial<Pumps> | null | undefined,
  tubingOuterDiameterInches: number | null | undefined,
  boltOnDischarges: Bolt_On_Discharges[] | null | undefined
): Bolt_On_Discharges | null {
  const matches = findMatchingDischarges(
    pump,
    tubingOuterDiameterInches,
    boltOnDischarges
  );
  return matches.length === 1 ? matches[0] : null;
}

export const DISCHARGE_OUTER_DIAMETER_TOLERANCE = 0.001;

/**
 * True when the primary, the secondary or the tertiary outer diameter of an HPS
 * bolt-on discharge fits the pump housing outer diameter.
 *
 * The catalog holds the three diameters in outer_diameter_inches,
 * neck_outer_diameter_inches and neck_inner_diameter_inches. The admin table
 * shows them as Primary OD, Secondary OD and Tertiary OD. HPS Suction reads the
 * same three fields the same way, in matchesAnySuctionOuterDiameter.
 *
 * A null or an undefined diameter means the catalog holds no value, so it is
 * skipped. A zero is a measurement and is compared. Most catalog rows carry a
 * primary diameter alone until an admin adds the other two, and such a row must
 * still match on the diameter it has.
 */
export function matchesAnyDischargeOuterDiameter(
  boltOnDischarge: Partial<Bolt_On_Discharges> | null | undefined,
  pumpHousingOuterDiameterInches: number | null | undefined
): boolean {
  if (!boltOnDischarge) return false;
  const target = Number(pumpHousingOuterDiameterInches);
  if (
    pumpHousingOuterDiameterInches === null ||
    pumpHousingOuterDiameterInches === undefined ||
    !Number.isFinite(target)
  ) {
    return false;
  }

  return [
    boltOnDischarge.outer_diameter_inches,
    boltOnDischarge.neck_outer_diameter_inches,
    boltOnDischarge.neck_inner_diameter_inches
  ]
    .filter(isBoltOnDischargeMeasurementKnown)
    .some(
      value =>
        Math.abs(Number(value) - target) < DISCHARGE_OUTER_DIAMETER_TOLERANCE
    );
}
